"use client";

import PageWrapper from "../components/ui/PageWrapper";

export default function PrivacyPolicy() {
  return (
    <PageWrapper maxWidth="4xl">
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-8 md:p-12">
        <h1 className="text-3xl font-bold text-widertalk-navy mb-8">Privacy Policy</h1>

        <div className="text-sm text-gray-600 mb-8">
          <p><strong>Last updated:</strong> August 1, 2025</p>
          <p><strong>Effective date:</strong> August 1, 2025</p>
        </div>

        <div className="prose prose-lg max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Welcome to WiderTalk ("we," "our," or "us"). We are committed to protecting your privacy and ensuring transparency about how we collect, use, and protect your personal information. This Privacy Policy explains our practices regarding data collection and use when you access or use our live sports commentary platform.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By using WiderTalk, you agree to the collection and use of information in accordance with this Privacy Policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-medium text-widertalk-navy mb-3">2.1 Account and Profile Information</h3>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
              <p className="text-gray-700">
                <strong>What we collect:</strong> Display name, email address, preferred language, nationality, profile description, and profile picture.
              </p>
              <p className="text-gray-700 mt-2">
                <strong>How we use it:</strong> To create and manage your account, personalize your experience, and display your commentary profile to other users.
              </p>
            </div>

            <h3 className="text-xl font-medium text-widertalk-navy mb-3">2.2 Authentication Data</h3>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
              <p className="text-gray-700">
                <strong>What we collect:</strong> Login credentials, authentication tokens, and session data managed through AWS Amplify.
              </p>
              <p className="text-gray-700 mt-2">
                <strong>How we use it:</strong> To securely authenticate your identity and maintain your login session.
              </p>
            </div>

            <h3 className="text-xl font-medium text-widertalk-navy mb-3">2.3 Commentary and Chat Data</h3>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
              <p className="text-gray-700">
                <strong>What we collect:</strong> Live commentary audio/video streams, chat messages, commentary ratings, and viewer engagement metrics.
              </p>
              <p className="text-gray-700 mt-2">
                <strong>How we use it:</strong> To provide the core functionality of our platform, enable live commentary features, and improve user experience.
              </p>
            </div>

            <h3 className="text-xl font-medium text-widertalk-navy mb-3">2.4 Technical and Usage Data</h3>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
              <p className="text-gray-700">
                <strong>What we collect:</strong> Device information, browser type, IP address, usage patterns, audio/video quality settings, and user preferences stored locally.
              </p>
              <p className="text-gray-700 mt-2">
                <strong>How we use it:</strong> To optimize platform performance, provide technical support, and enhance user experience.
              </p>
            </div>

            <h3 className="text-xl font-medium text-widertalk-navy mb-3">2.5 Local Storage Data</h3>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
              <p className="text-gray-700">
                <strong>What we collect:</strong> Welcome popup preferences, audio settings, expanded panel states, and other user interface preferences.
              </p>
              <p className="text-gray-700 mt-2">
                <strong>How we use it:</strong> To remember your preferences and provide a personalized experience across sessions.
              </p>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Provide and maintain our live commentary platform</li>
              <li>Process and display your commentary content</li>
              <li>Enable real-time chat and community features</li>
              <li>Personalize your user experience and content recommendations</li>
              <li>Analyze usage patterns to improve our services</li>
              <li>Provide customer support and respond to your inquiries</li>
              <li>Ensure platform security and prevent misuse</li>
              <li>Comply with legal obligations and enforce our terms of service</li>
            </ul>
          </section>

          {/* Data Sharing and Disclosure */}
          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">4. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
              <li><strong>Public Commentary:</strong> Your commentary content, display name, and nationality flag are visible to all platform users</li>
              <li><strong>Service Providers:</strong> With AWS and other technical service providers who help us operate our platform</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect our legal rights</li>
              <li><strong>Safety:</strong> To protect the safety and security of our users and platform</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">5. Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure authentication through AWS Amplify, and regular security assessments. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">6. Data Retention</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li><strong>Account Data:</strong> Retained while your account is active and for a reasonable period after account deletion</li>
              <li><strong>Commentary Content:</strong> Retained to maintain platform functionality and user experience</li>
              <li><strong>Chat Messages:</strong> Retained for moderation purposes and user experience</li>
              <li><strong>Analytics Data:</strong> Aggregated and anonymized data may be retained indefinitely for business insights</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">7. Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Objection:</strong> Object to certain processing activities</li>
              <li><strong>Withdrawal:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise these rights, please contact us using the information provided in Section 10.
            </p>
          </section>

          {/* Cookies and Tracking */}
          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">8. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use cookies and similar technologies to enhance your experience:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li><strong>Essential Cookies:</strong> Required for authentication, security, and core functionality</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Analytics Cookies:</strong> Help us understand usage patterns and improve our services</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              You can manage your cookie preferences through our cookie consent banner or your browser settings.
            </p>
          </section>

          {/* International Data Transfers */}
          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">9. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with applicable data protection laws.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">10. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700"><strong>Email:</strong> <a href={"mailto:privacy@widertalk.com"}>privacy@widertalk.com</a></p>
              <p className="text-gray-700"><strong>Website:</strong> Use the feedback form on our platform</p>
              <p className="text-gray-700"><strong>Response Time:</strong> We will respond to your inquiry within 30 days</p>
            </div>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">11. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          {/* Beta Notice */}
          <section className="bg-orange-50 border-l-4 border-orange-400 p-6">
            <h2 className="text-xl font-semibold text-orange-800 mb-3">Beta Platform Notice</h2>
            <p className="text-orange-700 leading-relaxed">
              WiderTalk is currently in beta. During this phase, we may collect additional feedback and usage data to improve our platform. All data collection practices described in this Privacy Policy apply to our beta version. We appreciate your participation in helping us build a better commentary platform.
            </p>
          </section>
        </div>
      </div>
    </PageWrapper>
  );
}
